import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import DashboardModal from './DashboardModal';

/** Trinity `SunlightIframeModelComponent` / `GoodLeapIframeModelComponent` — partner iframe in modal. */
export function SunlightIframeModal({
    open,
    title,
    url,
    onClose,
    width = 'min(90vw, 1000px)',
    iframeHeight = 'min(78vh, 720px)',
    centered = false,
    /** Match `AgreementsModal` shell (`.obd-agreements-modal-root` + theme) — same modal background/border as Agreements. */
    matchAgreementsModal = false,
    theme,
    zIndex,
    /** Shown over the iframe until `load` (e.g. Good Leap application page). */
    loadingText = 'Loading…',
}: {
    open: boolean;
    title: string;
    url: string | null;
    onClose: () => void;
    /** e.g. `80vw` for Good Leap (`good-leap-renderer.viewApplicationLink`) */
    width?: string | number;
    iframeHeight?: string;
    /** Vertically center modal (Trinity Good Leap iframe) */
    centered?: boolean;
    matchAgreementsModal?: boolean;
    theme?: 'light' | 'dark';
    zIndex?: number;
    loadingText?: string;
}) {
    const [iframeLoading, setIframeLoading] = useState(false);

    useEffect(() => {
        if (open && url) {
            setIframeLoading(true);
        } else {
            setIframeLoading(false);
        }
    }, [open, url]);
    /** Ant `width` is overridden by `.obd-agreements-modal.ant-modal` in AgreementsModal.css — partner iframe uses `.obd-partner-iframe-modal` there for real sizing. */
    const effectiveWidth = matchAgreementsModal ? 1000 : width;

    const modalClassName = matchAgreementsModal && theme
        ? `obd-sync-modal obd-agreements-modal obd-partner-iframe-modal obd-theme-${theme}`
        : undefined;
    const rootClassName = matchAgreementsModal && theme
        ? `obd-sync-modal-root obd-agreements-modal-root obd-theme-${theme}`
        : undefined;

    const iframeStyle: React.CSSProperties = matchAgreementsModal
        ? {
              width: '100%',
              minHeight: 'min(calc(100vh - 300px), 900px)',
              height: 'calc(100vh - 300px)',
              border: 'none',
              display: 'block',
          }
        : {
              width: '100%',
              height: iframeHeight,
              border: 'none',
          };

    return (
        <DashboardModal
            open={open}
            centered={centered}
            title={title}
            onClose={onClose}
            footer={null}
            width={effectiveWidth}
            zIndex={zIndex}
            theme={theme}
            className={modalClassName}
            rootClassName={rootClassName}
            destroyOnClose
        >
            {url ? (
                <div
                    className={
                        matchAgreementsModal
                            ? 'obd-partner-iframe-modal__frame-wrap'
                            : 'obd-partner-iframe-modal__frame-wrap obd-partner-iframe-modal__frame-wrap--simple'
                    }
                >
                    {iframeLoading ? (
                        <div
                            className="obd-partner-iframe-modal__loader"
                            role="status"
                            aria-live="polite"
                            aria-busy="true"
                        >
                            <Spin size="default" className="obd-partner-iframe-modal__loader-spin" />
                            <p className="obd-partner-iframe-modal__loader-text">{loadingText}</p>
                        </div>
                    ) : null}
                    <iframe
                        title={title}
                        src={url}
                        style={iframeStyle}
                        onLoad={() => setIframeLoading(false)}
                        onError={() => setIframeLoading(false)}
                    />
                </div>
            ) : null}
        </DashboardModal>
    );
}

export default SunlightIframeModal;
